import { renderErrorMessages } from "@/main/services/utils";
import React, { useEffect } from "react";
import {
	Control,
	Controller,
	ControllerFieldState,
	ControllerProps,
	ControllerRenderProps,
	FieldValues,
	UseFormSetValue,
	UseFormStateReturn,
} from "react-hook-form";
import KogInputText from "./inputText";
import KogInputTextOutlined from "./inputTextOutlined";

type IControllerProps = Omit<ControllerProps, "render" | "name">;
type IHTMLInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "number" | "color" | "size">;
interface IKogInputAmountControlledProps {
	id: string;
	name: string;
	control: Control<FieldValues>;
	typeInput?: "outlined" | "filled";
	required?: boolean;
	errorMessageCustom?: string;
	controllerProps?: IControllerProps;
	label?: string;
	customInputProps?: IHTMLInputProps;
	disabledMessage?: boolean;
	hideValue?: boolean;
	setValueCustom?: UseFormSetValue<any>;
	onChangeCustom?: (value: string) => void;
}

/**
 * Component to render a controlled input with currency mask.
 *
 * @param {string} id - Id of the input
 * @param {string} name - Name of the input
 * @param {Control<FieldValues>} control - Control of the form
 * @param {boolean} required - If true, the input will be required
 * @param {string} errorMessageCustom - Custom error message
 * @param {string} label - Label of the input
 * @param {IControllerProps} controllerProps - Props of the controller
 * @param {string} typeInput - Type of the input
 * @param {React.InputHTMLAttributes<HTMLInputElement>} customInputProps - Custom props of the input
 * @param {boolean} disabledMessage - If true, the error message will be disabled
 * @param {boolean} hideValue - If true, the value will be hidden
 * @param {Function} onChangeCustom - Function to be executed when the value changes
 * @example
 * const { control } = useForm(); //react-hoook-form
 *
 *  <KogInputAmountControlled
 *    id='inputtest'
 *    name="inputtest"
 *    control={control}
 *  />
 */
function KogInputIPMaskControlled({
	id,
	name,
	control,
	required = false,
	errorMessageCustom,
	typeInput = "outlined",
	label = "",
	controllerProps = {},
	customInputProps = {},
	disabledMessage = false,
	onChangeCustom = () => {},
	hideValue = false,
	setValueCustom,
}: IKogInputAmountControlledProps) {
	function MaskIP(value: string, oldValue: string, _event: any) {
		const lastChar = value.at(-1);
		const valueWithoutLastChar = value.substring(0, value.length - 1);
		const accDots = value.split("").filter((item) => item === ".").length;
		if (value === "." || value.endsWith("..")) return oldValue;
		const parts = value.split(".");
		const isInvalidPart = (part: string): boolean =>
			part === "00" || (part.length > 1 && part[0] === "0") || Number(part) < 0 || Number(part) > 255;
		if (parts.length > 4 || parts.some(isInvalidPart)) {
			if (accDots < 3) {
				const dotBeforeValue = valueWithoutLastChar + "." + lastChar;
				return dotBeforeValue;
			}
			return oldValue;
		}
		return value;
	}
	const useRenderInput = ({
		field,
		fieldState: { error },
	}: {
		field: ControllerRenderProps<FieldValues, string>;
		fieldState: ControllerFieldState;
		formState: UseFormStateReturn<FieldValues>;
	}) => {
		/**
		 * Notation:
		 * If the value is a number, it's because a value that came from the database,
		 * so it needs to be formatted as currency.
		 */
		const validateValue = field.value;

		const onChangeInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const oldValue = field.value;
			const validateValue = MaskIP(e.target.value, oldValue, e);

			// Notation: This is to keep the cursor in the same position
			const hasLastIndexChar = e.target.selectionStart === e.target.value.length;
			const positionChanged = hasLastIndexChar ? validateValue.length : e.target.selectionStart;
			e.target.value = validateValue;
			e.target.setSelectionRange(positionChanged, positionChanged);

			field.onChange(validateValue);
			onChangeCustom(validateValue);
		};

		// Notation: Use this method to prevent invalid ips

		const baseInputProps: IHTMLInputProps = {
			...field,
			...customInputProps,
			id: id,
			required: required,
			value: hideValue ? "" : validateValue,
			onChange: onChangeInput,
		};
		// UseEffect's
		useEffect(() => {
			if (validateValue && setValueCustom) {
				setValueCustom(name, validateValue);
			}
		}, [validateValue]);

		switch (typeInput) {
			case "outlined":
				return <KogInputTextOutlined {...baseInputProps} error={!!error || !!errorMessageCustom} />;
			case "filled":
				return (
					<KogInputText
						{...baseInputProps}
						label={label}
						error={!!error || !!errorMessageCustom}
						helperText={!disabledMessage && (errorMessageCustom || renderErrorMessages(label, error))}
						forceShrink
					/>
				);
			default:
				return <input {...baseInputProps} />;
		}
	};
	const customControllerProps: IControllerProps = {
		...controllerProps,
		...{ rules: { required } },
	};

	return (
		<Controller name={name} control={control} defaultValue={""} {...customControllerProps} render={useRenderInput} />
	);
}

export default KogInputIPMaskControlled;
